import axios from "axios";
import type { Action } from "@reduxjs/toolkit";
import type { ThunkAction } from "redux-thunk";
import type { AppState } from "@Store/index";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import {
  buildBlogArticlesListQuery,
  toBlogCardPost,
  type BlogCardPostResponse,
} from "@Pages/Blog/BlogHomeRevamp/utils/blogCardPost";
import { BLOG_ARTICLES_COLLECTION } from "@Pages/Blog/BlogHomeRevamp/settings";
import {
  listingPageFailure,
  listingPageRequest,
  listingPageSuccess,
  setActiveListing,
} from "./blogAllPostsReducer";

export type BlogListingQuery = {
  key: string;
  pageSize: number;
  categorySlug?: string;
  stageSlug?: string;
  countrySlug?: string;
};

type PaginatedDocsResponse = {
  docs: BlogCardPostResponse[];
  page: number;
  hasNextPage: boolean;
};

const fetchBlogListingPage =
  (
    { key, pageSize, categorySlug, stageSlug, countrySlug }: BlogListingQuery,
    page: number,
  ): ThunkAction<Promise<boolean>, AppState, unknown, Action<string>> =>
  async (dispatch) => {
    dispatch(listingPageRequest(key));
    try {
      const { data } = await axios.get<PaginatedDocsResponse>(
        `${getBlogCollectionUrl(BLOG_ARTICLES_COLLECTION)}?${buildBlogArticlesListQuery(
          { countrySlug, categorySlug, stageSlug },
          { limit: pageSize, page },
        )}`,
      );
      dispatch(
        listingPageSuccess({
          key,
          posts: (data?.docs || []).map(toBlogCardPost),
          page,
          hasNextPage: !!data?.hasNextPage,
        }),
      );
      return true;
    } catch (error) {
      console.error("Error fetching blog listing page:", error);
      dispatch(listingPageFailure(key));
      return false;
    }
  };

export const loadFirstListingPage =
  (
    scope: string,
    query: BlogListingQuery,
  ): ThunkAction<Promise<boolean | null>, AppState, unknown, Action<string>> =>
  async (dispatch, getState) => {
    dispatch(setActiveListing({ scope, key: query.key }));
    const status = getState().blogAllPostsData.listings[query.key]?.status;
    if (status === "success" || status === "request") return null;
    return dispatch(fetchBlogListingPage(query, 1));
  };

export default fetchBlogListingPage;
